<template>
  <!-- Add / Edit User -->
  <div class="modal fade dq-user-modal" id="user_modal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-lg dq-user-dialog">
      <div class="modal-content dq-modal-content">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div
              class="dq-hero"
              :class="isEditing ? 'dq-hero-blue' : 'dq-hero-indigo'"
            >
              <i :class="isEditing ? 'ti ti-user-edit' : 'ti ti-user-plus'"></i>
            </div>
            <div>
              <h4 class="modal-title dq-title mb-0">
                {{ isEditing ? "Edit User" : "Add User" }}
              </h4>
              <div class="dq-subtitle">
                {{
                  isEditing
                    ? "Update personal info, role and permissions."
                    : "Create a new user and assign access rights."
                }}
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x" />
          </button>
        </div>

        <form @submit.prevent="submitForm">
          <div class="modal-body dq-body pb-0">
            <!-- Top: quick summary -->
            <div class="dq-preview mb-3">
              <div class="dq-preview-left">
                <div class="dq-preview-title">
                  <i class="ti ti-eye me-1"></i> Preview
                </div>
                <div class="dq-preview-sub">
                  <span class="dq-name">
                    {{ fullNamePreview }}
                  </span>
                  <span class="dq-dot">•</span>
                  <span class="dq-pill" :class="statusPillClass">
                    <i class="ti ti-bolt"></i> {{ form.status || "Active" }}
                  </span>
                  <span class="dq-dot">•</span>
                  <span class="dq-pill dq-pill-role">
                    <i class="ti ti-shield"></i> {{ form.role || "No role" }}
                  </span>
                </div>
              </div>

              <div class="dq-preview-right">
                <span class="dq-mini">
                  <i class="ti ti-lock-access"></i>
                  {{ form.permissions?.length || 0 }} permission(s)
                </span>
              </div>
            </div>

            <div class="row g-3">
              <!-- LEFT COLUMN -->
              <div class="col-lg-6">
                <!-- Personal info -->
                <div class="dq-card">
                  <div class="dq-card-head">
                    <span class="dq-badge dq-badge-indigo">
                      <i class="ti ti-id"></i>
                    </span>
                    <div>
                      <div class="dq-h6">Personal Details</div>
                      <div class="dq-help">
                        Basic user identity information.
                      </div>
                    </div>
                  </div>

                  <div class="dq-card-body">
                    <div class="row g-3">
                      <div class="col-md-6">
                        <label class="form-label dq-label">
                          First Name <span class="text-danger">*</span>
                        </label>
                        <input
                          v-model.trim="form.first_name"
                          type="text"
                          class="form-control dq-input"
                          required
                          placeholder="e.g. Jane"
                        />
                      </div>

                      <div class="col-md-6">
                        <label class="form-label dq-label">Middle Name</label>
                        <input
                          v-model.trim="form.middle_name"
                          type="text"
                          class="form-control dq-input"
                          placeholder="Optional"
                        />
                      </div>

                      <div class="col-md-6">
                        <label class="form-label dq-label">
                          Last Name <span class="text-danger">*</span>
                        </label>
                        <input
                          v-model.trim="form.last_name"
                          type="text"
                          class="form-control dq-input"
                          required
                          placeholder="e.g. Owino"
                        />
                      </div>

                      <div class="col-md-6">
                        <label class="form-label dq-label"> Phone </label>
                        <input
                          v-model.trim="form.phone"
                          type="text"
                          class="form-control dq-input"
                          placeholder="e.g. +2547..."
                        />
                      </div>

                      <div class="col-12">
                        <label class="form-label dq-label">
                          Email <span class="text-danger">*</span>
                        </label>
                        <input
                          v-model.trim="form.email"
                          type="email"
                          class="form-control dq-input"
                          required
                          placeholder="e.g. name@company.com"
                        />
                      </div>

                      <!-- Password (add only) -->
                      <template v-if="!isEditing">
                        <div class="col-md-6">
                          <label class="form-label dq-label">
                            Password <span class="text-danger">*</span>
                          </label>
                          <input
                            v-model="form.password"
                            type="password"
                            class="form-control dq-input"
                            required
                            placeholder="Create a password"
                          />
                        </div>
                        <div class="col-md-6">
                          <label class="form-label dq-label">
                            Confirm Password <span class="text-danger">*</span>
                          </label>
                          <input
                            v-model="form.password_confirmation"
                            type="password"
                            class="form-control dq-input"
                            required
                            placeholder="Repeat password"
                          />
                        </div>
                      </template>
                    </div>
                  </div>
                </div>

                <!-- Account settings -->
                <div class="dq-card mt-3">
                  <div class="dq-card-head">
                    <span class="dq-badge dq-badge-blue">
                      <i class="ti ti-settings"></i>
                    </span>
                    <div>
                      <div class="dq-h6">Account Settings</div>
                      <div class="dq-help">
                        Role and status control access scope.
                      </div>
                    </div>
                  </div>

                  <div class="dq-card-body">
                    <div class="row g-3">
                      <div class="col-md-6">
                        <label class="form-label dq-label">Status</label>
                        <vue-select
                          v-model="form.status"
                          :options="['Active', 'Inactive']"
                          placeholder="Select status"
                        />
                        <div class="dq-hint">
                          Inactive users can’t sign in, but remain in audit
                          history.
                        </div>
                      </div>

                      <div class="col-md-6">
                        <label class="form-label dq-label">
                          Role <span class="text-danger">*</span>
                        </label>
                        <vue-select
                          :options="roleNames"
                          v-model="form.role"
                          placeholder="Select Role"
                          required
                        />
                        <div class="dq-hint">
                          Role usually defines default permissions.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- RIGHT COLUMN -->
              <div class="col-lg-6">
                <!-- Permissions -->
                <div class="dq-card dq-perms-card">
                  <div class="dq-card-head">
                    <span class="dq-badge dq-badge-purple">
                      <i class="ti ti-shield-lock"></i>
                    </span>
                    <div class="dq-head-row">
                      <div>
                        <div class="dq-h6">Permissions Matrix</div>
                        <div class="dq-help">
                          Fine-tune access per module and action.
                        </div>
                      </div>

                      <div class="dq-tools">
                        <button
                          type="button"
                          class="btn btn-sm btn-light dq-toolbtn"
                          @click="selectAll"
                        >
                          <i class="ti ti-checklist"></i>
                          Select All
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm btn-light dq-toolbtn"
                          @click="clearAll"
                        >
                          <i class="ti ti-eraser"></i>
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="dq-card-body p-0">
                    <div class="dq-perms-tablewrap">
                      <table class="table dq-perms-table mb-0">
                        <thead>
                          <tr>
                            <th class="dq-sticky-col dq-module-col">Module</th>
                            <th
                              v-for="a in actions"
                              :key="a"
                              class="dq-action-col"
                            >
                              <span class="dq-action-pill">{{ a }}</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="m in modules" :key="m">
                            <td class="dq-sticky-col dq-module-cell">
                              <span class="dq-module-chip">
                                <i class="ti ti-box"></i> {{ m }}
                              </span>
                            </td>

                            <td
                              v-for="a in actions"
                              :key="a"
                              class="dq-checkcell"
                            >
                              <label class="dq-check">
                                <input
                                  type="checkbox"
                                  :checked="
                                    form.permissions.includes(`${m}.${a}`)
                                  "
                                  @change="togglePermission(m, a)"
                                />
                                <span class="dq-check-ui"></span>
                              </label>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div class="dq-perms-footer">
                      <div class="dq-perms-summary">
                        <i class="ti ti-info-circle"></i>
                        Tip: Start with a role, then adjust specific
                        permissions.
                      </div>
                      <div class="dq-perms-count">
                        <span class="dq-count">{{
                          form.permissions.length
                        }}</span>
                        selected
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- /RIGHT -->
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-footer">
            <button
              type="button"
              class="btn btn-light dq-btn dq-btn-muted me-2"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>

            <button
              type="submit"
              class="btn btn-primary dq-btn dq-btn-primary"
              :disabled="isLoading"
            >
              <span
                v-if="isLoading"
                class="spinner-border spinner-border-sm me-2"
                role="status"
              ></span>
              {{ isEditing ? "Save Changes" : "Add User" }}
              <i class="ti ti-check ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete Modal (keep same logic; upgraded look slightly by CSS only) -->
  <div class="modal fade dq-user-modal" id="delete_modal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered dq-delete-dialog">
      <div class="modal-content dq-modal-content">
        <div class="modal-body text-center dq-delete-body">
          <span class="avatar avatar-xl bg-transparent-danger text-danger mb-3">
            <i class="ti ti-trash-x fs-36"></i>
          </span>
          <h4 class="mb-1 dq-title">Confirm Delete</h4>
          <p class="mb-3 dq-subtitle">
            Are you sure you want to delete
            <b>{{ userData?.first_name }} {{ userData?.last_name }}</b
            >? This action cannot be undone.
          </p>
          <div class="d-flex justify-content-center gap-2">
            <button class="btn btn-light dq-btn" data-bs-dismiss="modal">
              Cancel
            </button>
            <button
              class="btn btn-danger dq-btn"
              @click="confirmDelete"
              :disabled="isLoading"
              type="button"
            >
              <span
                v-if="isLoading"
                class="spinner-border spinner-border-sm me-2"
                role="status"
              ></span>
              Yes, Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from "vue";

import { useUserStore } from "@/stores/userStore";
import { useRoleStore } from "@/stores/roleStore";
import { useCatalogStore } from "@/stores/catalogStore";
import { toast } from "@/utils/toast";

const props = defineProps({
  userData: Object,
  isEditing: Boolean,
});
const emit = defineEmits(["saved"]);

const userStore = useUserStore();
const roleStore = useRoleStore();
const catalogStore = useCatalogStore();

// Fetch roles for the selector
onMounted(async () => {
  if (!roleStore.roles.length) await roleStore.fetchRoles();
  if (!catalogStore.loaded) await catalogStore.fetchPermissionsCatalog();
});

const roleNames = computed(() =>
  roleStore.roles
    .map((r) => r.name)
    .filter((name) => name && name !== "SuperAdmin")
);
const isLoading = computed(() => userStore.loading);

const form = ref({
  first_name: "",
  middle_name: "",
  last_name: "",
  email: "",
  password: "",
  password_confirmation: "",
  phone: "",
  status: "Active",
  role: "",
  permissions: [],
});

const modules = computed(() => catalogStore.moduleKeys);
const actions = computed(() => catalogStore.actionKeys);

watch(
  () => props.userData,
  (newVal) => {
    if (props.isEditing && newVal) {
      form.value = {
        id: newVal.id,
        first_name: newVal.first_name,
        middle_name: newVal.middle_name,
        last_name: newVal.last_name,
        email: newVal.email,
        password: "",
        password_confirmation: "",
        phone: newVal.phone,
        status: newVal.status,
        role: newVal.roles?.[0]?.name || "",
        permissions: newVal.permissions?.map((p) => p.name) || [],
      };
    } else {
      resetForm();
    }
  },
  { immediate: true }
);

function resetForm() {
  form.value = {
    first_name: "",
    middle_name: "",
    last_name: "",
    email: "",
    password: "",
    password_confirmation: "",
    phone: "",
    status: "Active",
    role: "",
    permissions: [],
  };
}

function togglePermission(module, action) {
  const p = `${module}.${action}`;
  const idx = form.value.permissions.indexOf(p);
  idx === -1
    ? form.value.permissions.push(p)
    : form.value.permissions.splice(idx, 1);
}

/* UX helpers */
const fullNamePreview = computed(() => {
  const a = [
    form.value.first_name,
    form.value.middle_name,
    form.value.last_name,
  ]
    .map((x) => (x || "").trim())
    .filter(Boolean);
  return a.length ? a.join(" ") : "— New user";
});
const statusPillClass = computed(() => {
  const s = String(form.value.status || "").toLowerCase();
  return s === "inactive" ? "dq-pill-muted" : "dq-pill-live";
});

function selectAll() {
  const all = [];
  for (const m of modules.value) {
    for (const a of actions.value) {
      all.push(`${m}.${a}`);
    }
  }
  form.value.permissions = all;
}
function clearAll() {
  form.value.permissions = [];
}

function hideModal() {
  const modalEl = document.getElementById("user_modal");
  if (modalEl) {
    modalEl.classList.remove("show");
    modalEl.style.display = "none";
    modalEl.setAttribute("aria-hidden", "true");
    modalEl.removeAttribute("aria-modal");
  }
  document.body.classList.remove("modal-open");
  document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
}

function closeModal(id) {
  const modalEl = document.getElementById(id);
  if (modalEl) {
    modalEl.classList.remove("show");
    modalEl.style.display = "none";
    modalEl.setAttribute("aria-hidden", "true");
    modalEl.removeAttribute("aria-modal");
  }
  document.body.classList.remove("modal-open");
  document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
}

async function submitForm() {
  try {
    if (props.isEditing) {
      await userStore.updateUser(form.value);
    } else {
      await userStore.createUser(form.value);
    }
    toast("User saved successfully!");
    emit("saved");
    hideModal();
  } catch (e) {
    toast(e.response?.data?.message || "Error saving user", "error");
  }
}

async function confirmDelete() {
  if (props.userData && props.userData.id) {
    await userStore.deleteUser(props.userData.id);
    emit("saved");
    closeModal("delete_modal");
  }
}
</script>

<style scoped>
/* Wider, world-class modal width */
.dq-user-dialog {
  max-width: 1080px;
}
@media (max-width: 1200px) {
  .dq-user-dialog {
    max-width: 96%;
  }
}
.dq-delete-dialog {
  max-width: 520px;
}

/* Content shell */
.dq-modal-content {
  border-radius: 22px;
  border: none;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08),
    0 40px 90px rgba(15, 23, 42, 0.14);
}

/* Header */
.dq-modal-header {
  padding: 18px 22px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.14), #fff);
}
.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-title {
  font-weight: 950;
  letter-spacing: -0.35px;
}
.dq-subtitle {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-close {
  opacity: 0.9;
}
.dq-close:hover {
  opacity: 1;
}

/* Hero icon */
.dq-hero {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.dq-hero-indigo {
  background: rgba(99, 102, 241, 0.16);
  color: rgb(79, 70, 229);
}
.dq-hero-blue {
  background: rgba(59, 130, 246, 0.14);
  color: rgb(37, 99, 235);
}

/* Body */
.dq-body {
  padding: 18px 22px 10px;
}

/* Preview */
.dq-preview {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(15, 23, 42, 0.02);
}
.dq-preview-title {
  font-weight: 950;
}
.dq-preview-sub {
  margin-top: 2px;
  color: rgba(15, 23, 42, 0.68);
  font-size: 0.86rem;
}
.dq-name {
  font-weight: 950;
}
.dq-dot {
  margin: 0 8px;
  opacity: 0.5;
}
.dq-mini {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  font-weight: 900;
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.78);
}

/* Pills */
.dq-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  border-radius: 999px;
  font-weight: 950;
  font-size: 0.78rem;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
}
.dq-pill-live {
  background: rgba(34, 197, 94, 0.1);
  color: rgb(22, 163, 74);
  border-color: rgba(34, 197, 94, 0.18);
}
.dq-pill-muted {
  background: rgba(15, 23, 42, 0.05);
  color: rgba(15, 23, 42, 0.75);
}
.dq-pill-role {
  background: rgba(99, 102, 241, 0.1);
  color: rgb(79, 70, 229);
  border-color: rgba(99, 102, 241, 0.18);
}

/* Card */
.dq-card {
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 18px;
  background: #fff;
  overflow: hidden;
}
.dq-card-head {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 14px 14px 0 14px;
}
.dq-head-row {
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.dq-card-body {
  padding: 14px;
}
.dq-h6 {
  font-weight: 950;
  letter-spacing: -0.2px;
}
.dq-help {
  font-size: 0.8rem;
  color: rgba(15, 23, 42, 0.55);
  line-height: 1.35;
}
.dq-hint {
  margin-top: 6px;
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.55);
}

/* Badge */
.dq-badge {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}
.dq-badge-indigo {
  background: rgba(99, 102, 241, 0.12);
  color: rgb(79, 70, 229);
}
.dq-badge-blue {
  background: rgba(59, 130, 246, 0.12);
  color: rgb(37, 99, 235);
}
.dq-badge-purple {
  background: rgba(168, 85, 247, 0.12);
  color: rgb(147, 51, 234);
}

/* Inputs */
.dq-label {
  font-size: 0.82rem;
  font-weight: 900;
}
.dq-input {
  border-radius: 14px;
  padding: 10px 12px;
  transition: all 0.15s ease;
}
.dq-input:focus {
  border-color: rgba(99, 102, 241, 0.55);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.14);
}

/* Permissions table */
.dq-perms-card .dq-card-body {
  padding: 0;
}
.dq-perms-tablewrap {
  max-height: 420px;
  overflow: auto;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-perms-table {
  width: 100%;
  table-layout: fixed;
}
.dq-perms-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  padding: 10px 12px;
}
.dq-module-col {
  width: 180px;
}
.dq-action-col {
  width: 90px;
  text-align: center;
}
.dq-action-pill {
  display: inline-flex;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 0.74rem;
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-sticky-col {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #fff;
}
.dq-module-cell {
  padding: 10px 12px !important;
  border-right: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-module-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 0.8rem;
  background: rgba(99, 102, 241, 0.08);
  color: rgba(15, 23, 42, 0.85);
  border: 1px solid rgba(99, 102, 241, 0.14);
}
.dq-checkcell {
  text-align: center;
  vertical-align: middle;
  padding: 10px 0 !important;
}

/* custom checkbox */
.dq-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 12px;
  cursor: pointer;
  user-select: none;
}
.dq-check input {
  display: none;
}
.dq-check-ui {
  width: 18px;
  height: 18px;
  border-radius: 6px;
  border: 2px solid rgba(15, 23, 42, 0.25);
  display: inline-block;
  transition: all 0.14s ease;
  position: relative;
}
.dq-check input:checked + .dq-check-ui {
  border-color: rgba(99, 102, 241, 0.8);
  background: rgba(99, 102, 241, 0.18);
}
.dq-check input:checked + .dq-check-ui::after {
  content: "";
  position: absolute;
  left: 4px;
  top: 1px;
  width: 6px;
  height: 10px;
  border: solid rgba(99, 102, 241, 0.95);
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.dq-check:hover .dq-check-ui {
  border-color: rgba(99, 102, 241, 0.55);
}

/* tools */
.dq-tools {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.dq-toolbtn {
  border-radius: 12px;
  font-weight: 900;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.dq-perms-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(15, 23, 42, 0.02);
}
.dq-perms-summary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgba(15, 23, 42, 0.7);
  font-size: 0.82rem;
  font-weight: 700;
}
.dq-perms-count {
  font-weight: 900;
  color: rgba(15, 23, 42, 0.8);
}
.dq-count {
  display: inline-flex;
  min-width: 30px;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
}

/* Footer */
.dq-footer {
  padding: 14px 22px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
}
.dq-btn {
  border-radius: 12px;
  padding: 10px 16px;
  font-weight: 900;
}
.dq-btn-primary {
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.1);
}

/* delete body spacing */
.dq-delete-body {
  padding: 22px;
}
</style>
